/**
 * §B7 — TOTP enrolment, on the Account screen.
 *
 * The flow mirrors the API's own lifecycle, each step re-proving a factor:
 *
 *   1. `POST /auth/totp/enroll { password }` — a live session alone may not
 *      start changing the second factor.
 *   2. The secret is shown ONCE, as a copyable grouped code plus the full
 *      `otpauth://` URL. **No QR code is drawn.** A correct QR encoder needs
 *      Reed–Solomon codewords, format masking and penalty scoring — hundreds
 *      of lines whose only failure mode is a silently unscannable image — and
 *      the house rules allow no new dependency. Every mainstream authenticator
 *      app has an "enter a setup key" path, so the copyable secret is the
 *      honest, verifiable rendering.
 *   3. `POST /auth/totp/confirm { code }` proves the app produces valid codes
 *      before anything is enforced.
 *   4. The recovery codes are shown exactly once, behind a copy-all button and
 *      a "stored safely" confirmation gate — they exist nowhere else, and the
 *      server keeps only hashes.
 *
 * Turning it off requires fresh proof of BOTH factors (`DELETE /auth/totp
 * { password, code }`), collected inline and then confirmed through the
 * Phase 04 `ConfirmDialog` primitive — the dialog decides how a destructive
 * confirmation looks, not this screen (criterion 25).
 */

import type { TotpConfirmResponse, TotpEnrollResponse } from '@rd/shared';
import { Copy, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent, type ReactElement } from 'react';

import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../components/Button.js';
import { Field } from '../../components/Field.js';
import { Input } from '../../components/Input.js';
import { ConfirmDialog } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { apiRequest } from '../../lib/apiClient.js';
import { copyFor } from '../../lib/errorCopy.js';

/** `ABCD EFGH …` — the grouping every authenticator's manual-entry field shows. */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

type EnrolStep =
  | { step: 'password' }
  | { step: 'confirm'; enroll: TotpEnrollResponse }
  | { step: 'recovery'; codes: string[] };

export function SecuritySection(): ReactElement | null {
  const { security, refreshMe } = useAuth();
  const toast = useToast();

  const [enrol, setEnrol] = useState<EnrolStep | null>(null);
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [stored, setStored] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  // The turn-off flow's own state, so abandoning it cannot leak into enrolment.
  const [disabling, setDisabling] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [confirmingDisable, setConfirmingDisable] = useState(false);

  // Null until `/auth/me` has answered — a section that guesses the security
  // posture would flash the wrong controls during boot.
  if (!security) return null;

  const copy = error ? copyFor(error) : null;

  const copyText = async (value: string, what: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      toast.push('success', `${what} copied.`);
    } catch {
      toast.push('error', `Could not copy the ${what.toLowerCase()} — select and copy it by hand.`);
    }
  };

  const startEnrol = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const enroll = await apiRequest<TotpEnrollResponse>('/auth/totp/enroll', {
        method: 'POST',
        body: { password },
      });
      // The password has done its job; keeping it in state past this line
      // would only widen what a snapshot of the page could leak.
      setPassword('');
      setEnrol({ step: 'confirm', enroll });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const confirmEnrol = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await apiRequest<TotpConfirmResponse>('/auth/totp/confirm', {
        method: 'POST',
        body: { code: code.trim() },
      });
      setCode('');
      setStored(false);
      setEnrol({ step: 'recovery', codes: result.recoveryCodes });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const finishEnrol = async (): Promise<void> => {
    setBusy(true);
    try {
      // `/auth/me` is what flips `security.totpEnabled`; the codes leave state
      // here and are shown never again — the server holds only hashes.
      await refreshMe();
    } finally {
      setBusy(false);
      setEnrol(null);
      toast.push('success', 'Two-factor authentication is on.');
    }
  };

  const disable = async (): Promise<void> => {
    setBusy(true);
    try {
      await apiRequest('/auth/totp', {
        method: 'DELETE',
        body: { password: disablePassword, code: disableCode.trim() },
      });
      setConfirmingDisable(false);
      setDisabling(false);
      setDisablePassword('');
      setDisableCode('');
      await refreshMe();
      toast.push('success', 'Two-factor authentication is off.');
    } catch (err) {
      // The dialog closes but the fields stay: the person decides whether to
      // re-enter a fresh code or abandon, rather than losing both inputs.
      setConfirmingDisable(false);
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-labelledby="account-security-heading" className="mb-10">
      <h2 id="account-security-heading" className="mb-3 text-sm font-semibold text-body">
        Security
      </h2>

      <div className="flex flex-col gap-4 rounded-[var(--brand-radius)] border border-line bg-surface p-6">
        {copy ? (
          <p role="alert" className="text-sm text-body">
            <strong className="font-medium">{copy.title}</strong> {copy.body}
          </p>
        ) : null}

        {security.totpEnabled ? (
          <>
            <div className="flex items-start gap-[var(--space-2)]">
              <ShieldCheck
                aria-hidden="true"
                size={16}
                strokeWidth={2}
                className="mt-[2px] shrink-0 text-success-text"
              />
              <div>
                <p className="text-sm font-medium text-body">Two-factor authentication is on</p>
                <p className="text-sm text-muted">
                  Signing in asks for a code from your authenticator app.
                </p>
              </div>
            </div>

            {disabling ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setConfirmingDisable(true);
                }}
                className="flex max-w-sm flex-col gap-[var(--space-3)]"
                noValidate
              >
                {/* Fresh proof of both factors — a live session alone may not
                    remove the second factor (auth.routes.ts §B7). */}
                <Field label="Password" required>
                  {(props) => (
                    <Input
                      {...props}
                      type="password"
                      autoComplete="current-password"
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Verification code" required>
                  {(props) => (
                    <Input
                      {...props}
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={disableCode}
                      onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, ''))}
                      className="font-mono"
                    />
                  )}
                </Field>
                <div className="flex gap-[var(--space-2)]">
                  <Button type="submit" variant="secondary">
                    Turn off two-factor authentication
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDisabling(false);
                      setDisablePassword('');
                      setDisableCode('');
                      setError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            ) : (
              <Button variant="secondary" className="self-start" onClick={() => setDisabling(true)}>
                Turn off
              </Button>
            )}
          </>
        ) : enrol === null ? (
          <>
            <p className="text-sm text-body">
              Two-factor authentication is off. Add an authenticator app so signing in needs a code
              as well as your password.
            </p>
            <Button className="self-start" onClick={() => setEnrol({ step: 'password' })}>
              Set up authenticator app
            </Button>
          </>
        ) : enrol.step === 'password' ? (
          <form onSubmit={(e) => void startEnrol(e)} className="flex max-w-sm flex-col gap-[var(--space-3)]" noValidate>
            <p className="text-sm text-body">Confirm your password to begin.</p>
            <Field label="Password" required>
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
            <div className="flex gap-[var(--space-2)]">
              <Button type="submit" loading={busy}>
                Continue
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setEnrol(null);
                  setPassword('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : enrol.step === 'confirm' ? (
          <form onSubmit={(e) => void confirmEnrol(e)} className="flex max-w-lg flex-col gap-[var(--space-3)]" noValidate>
            <ol className="flex list-decimal flex-col gap-[var(--space-2)] pl-5 text-sm text-body">
              <li>
                Open your authenticator app and choose{' '}
                <strong className="font-medium">enter a setup key</strong> (sometimes called manual
                entry).
              </li>
              <li>Enter the account email and this secret key, set to time-based.</li>
              <li>Enter the 6-digit code the app shows to finish.</li>
            </ol>

            <div className="flex flex-col gap-[var(--space-1)]">
              <span className="text-sm font-medium text-body">Secret key</span>
              <div className="flex items-center gap-[var(--space-2)]">
                <output
                  data-testid="totp-secret"
                  className="select-all break-all rounded-[var(--radius-md)] border border-line bg-surface-alt px-3 py-2 font-mono text-sm text-body"
                >
                  {groupSecret(enrol.enroll.secret)}
                </output>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Copy}
                  onClick={() => void copyText(enrol.enroll.secret, 'Secret key')}
                >
                  Copy
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-[var(--space-1)]">
              <span className="text-sm font-medium text-body">Setup link</span>
              <div className="flex items-center gap-[var(--space-2)]">
                <output className="select-all break-all rounded-[var(--radius-md)] border border-line bg-surface-alt px-3 py-2 font-mono text-[length:var(--text-xs)] text-muted">
                  {enrol.enroll.otpauthUrl}
                </output>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={Copy}
                  onClick={() => void copyText(enrol.enroll.otpauthUrl, 'Setup link')}
                >
                  Copy
                </Button>
              </div>
              <p className="text-[length:var(--text-xs)] text-muted">
                Some apps accept this link pasted directly instead of the key.
              </p>
            </div>

            <Field label="6-digit code from the app" required>
              {(props) => (
                <Input
                  {...props}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="max-w-40 font-mono tracking-[0.25em]"
                />
              )}
            </Field>

            <div className="flex gap-[var(--space-2)]">
              <Button type="submit" loading={busy}>
                Confirm
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setEnrol(null);
                  setCode('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex max-w-lg flex-col gap-[var(--space-3)]">
            <p className="text-sm font-medium text-body">
              Save these recovery codes now — they will not be shown again.
            </p>
            <p className="text-sm text-muted">
              Each one signs you in exactly once if you lose your authenticator. Keep them
              somewhere safe and private, not in this browser.
            </p>
            <ul
              data-testid="recovery-codes"
              className="grid grid-cols-1 gap-[var(--space-1)] rounded-[var(--radius-md)] border border-line bg-surface-alt p-4 font-mono text-sm text-body sm:grid-cols-2"
            >
              {enrol.codes.map((recovery) => (
                <li key={recovery} className="select-all">
                  {recovery}
                </li>
              ))}
            </ul>
            <Button
              variant="secondary"
              className="self-start"
              icon={Copy}
              onClick={() => void copyText(enrol.codes.join('\n'), 'Recovery codes')}
            >
              Copy all codes
            </Button>
            <label className="flex items-start gap-[var(--space-2)] text-sm text-body">
              <input
                type="checkbox"
                checked={stored}
                onChange={(e) => setStored(e.target.checked)}
                className="mt-[2px] size-4 shrink-0 accent-primary"
              />
              I have stored these codes safely
            </label>
            {/* The gate: losing the authenticator AND the codes is unrecoverable
                without an administrator, so "Done" is earned, not defaulted. */}
            <Button className="self-start" disabled={!stored} loading={busy} onClick={() => void finishEnrol()}>
              Done
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmingDisable}
        title="Turn off two-factor authentication?"
        body="Signing in will only need your password again, and your recovery codes will stop working. This makes the account easier to break into."
        confirmLabel="Turn it off"
        destructive
        busy={busy}
        onConfirm={() => void disable()}
        onCancel={() => setConfirmingDisable(false)}
      />
    </section>
  );
}
